// Task #7103 — Promise.prototype.finally polyfill for very old browsers
// (Android 4–5 era WebViews, pre-2018 Safari/Chrome). Sentry showed recurring
// boot-time crashes ("....then(...).catch(...).finally is not a function")
// because those engines predate ES2018's Promise.prototype.finally. The SPA
// crashed before render for those visitors.
//
// This module MUST be the first import in main.tsx: ES module imports are
// hoisted and evaluated in order, so anything using `.finally` at module
// evaluation time only sees the polyfill if this file runs before it.
//
// Spec-faithful minimal implementation: the callback runs on both settle
// paths, receives no arguments, the original value/reason is passed through,
// and a throwing/rejecting callback replaces the outcome — all achieved by
// delegating to `.then`, exactly like the spec's fallback semantics.
/* eslint-disable @typescript-eslint/no-explicit-any */
if (
  typeof Promise !== "undefined" &&
  typeof (Promise.prototype as any).finally !== "function"
) {
  // Not enumerable (matches the native property descriptor) so for-in loops
  // and Object.keys over promise instances/prototypes stay unchanged.
  Object.defineProperty(Promise.prototype, "finally", {
    value: function finallyPolyfill(
      this: Promise<unknown>,
      onFinally?: (() => unknown) | null,
    ): Promise<unknown> {
      // Use this promise's own constructor (subclass-safe), falling back to
      // Promise itself if it's been tampered with.
      const C = (this?.constructor as PromiseConstructor) || Promise;
      const cb = typeof onFinally === "function" ? onFinally : undefined;
      if (!cb) {
        // Per spec, a non-callable onFinally is passed straight through.
        return this.then(onFinally as any, onFinally as any);
      }
      return this.then(
        (value) => C.resolve(cb()).then(() => value),
        (reason) =>
          C.resolve(cb()).then(() => {
            throw reason;
          }),
      );
    },
    writable: true,
    configurable: true,
  });
}

export {};
